import type { CustomField } from "@lib/constant";

import type { AppointmentFormValues } from "@components/booking/pages/BookingPage";

type UserParam = {
  userName: string | null;
};

export type Provider = {
  id: number;
  firstName: string;
  lastName: string;
};

export type Operatory = {
  id: number;
  name: string;
};

export type AppointmentType = {
  id: number;
  name: string;
};

export type PatientSearchParam = {
  firstName: string;
  lastName: string;
  birthday?: string;
  email?: string;
  phone?: string;
  userName: string | null;
};

export type PatientCreateParam = {
  firstName: string;
  lastName: string;
  birthday: string;
  email: string;
  phone: string;
  userName: string | null;
  providerId?: number | null;
};

export type Patient = {
  patientId: number;
  firstName: string;
  lastName: string;
  birthDate: string;
  email: string;
  phone: string;
  cellPhone: string;
  homePhone: string;
  workPhone: string;
};

export type AvailabilityParam = {
  startDate: string;
  endDate: string;
  providerIds: number[];
  operatories: number[];
  weekendOps: number[];
  duration: number;
  slotInterval: number;
  userName: string;
  apptTypeId: number | null;
  reasonId: number;
  allowProviderOnMultiOps: boolean;
  minLeadTime: number;
  maxLeadTime: number;
  useHourSlot: boolean;
};

export type TimeSlot = {
  startTime: string;
  endTime: string;
  providerId: number;
  operatoryId: number;
};

export type CreateAppointmentParam = {
  patientId: number;
  operatoryId: number;
  providerId: number;
  apptTypeId: number;
  appointmentTime: string;
  duration: number;
  isNewPatient: boolean;
  note: string;
  userName: string | null;
};

export type UpdateAppointmentNoteParam = {
  id: number;
  note: string;
  userName: string | null;
};

export type Appointment = {
  appointmentId: number;
  startTime: string;
  patientId: number;
  providerId: number;
  operatoryId: number;
};

export type ConfirmEmailParam = {
  values: AppointmentFormValues;
  customField: CustomField;
  appointmentDate: string | null;
  appointmentType: string;
  status: boolean;
  eventTypeId: number;
  insurance: string;
  secondaryInsurance: string;
  userName: string | null;
};

export type VerificationEmailParam = {
  appointmentDate: string | null;
  providerId: number;
  reason: string;
  eventTypeId: number;
  token: string;
  to: string;
  userName: string | null;
};

export type BookingEmailParam = {
  appointmentId: number;
  appointmentType: string;
  paymentMethod: string;
  insurancePhone: string;
  memberId: string;
  groupNumber: string;
  employerName: string;
  subscriberName: string;
  subscriberDOB: string;
  postalCode: string;
  ssn: string;
  userName: string | null;
};

export type CreateAvailabilityParam = {
  availabilityId?: number | null;
  providerId?: number | null;
  operatoryId: number;
  apptTypeId: number;
  beginTime: string;
  endTime: string;
  days?: number[];
  specificDate?: string;
  userName: string | null;
};

export type DeleteAvailabilityParam = {
  availabilityId?: number | null;
  userName: string | null;
};

export type Availability = {
  id: number;
  providerId: number;
  operatoryId: number;
};

export type ClientSecret = {
  clientSecret: string;
};

export type CommLog = {
  CommlogNum: number;
  PatNum: number;
  CommDateTime: string;
  CommType: string;
  Note: string;
  SentOrReceived: string;
};

export type CheckAvailabilityParam = {
  appointmentTime: string;
  operatoryId: number;
  duration: number;
  userName: string | null;
  reasonId?: number;
  providerId?: number;
};

export const checkAvailability = async (param: CheckAvailabilityParam): Promise<boolean> => {
  try {
    const response = await fetch("/api/practice/appointments/checkAvailability", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const createAppointment = async (param: CreateAppointmentParam): Promise<Appointment> => {
  try {
    const response = await fetch("/api/practice/appointments/create", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const updateAppointmentNote = async (param: UpdateAppointmentNoteParam): Promise<boolean> => {
  try {
    const response = await fetch("/api/practice/appointments/updateNote", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const createCommunicationLog = async (param: UpdateAppointmentNoteParam): Promise<CommLog> => {
  try {
    const response = await fetch("/api/practice/commlog", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const sendVerificationEmail = async (param: VerificationEmailParam): Promise<{ message: string }> => {
  try {
    const response = await fetch("/api/email/verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const sendBookingEmail = async (param: BookingEmailParam): Promise<{ message: string }> => {
  try {
    const response = await fetch("/api/practice/email/notify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const sendConfirmEmail = async (param: ConfirmEmailParam): Promise<{ message: string }> => {
  try {
    const response = await fetch("/api/email/notify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const searchPatient = async (param: PatientSearchParam): Promise<Patient[]> => {
  try {
    const response = await fetch("/api/practice/patients/search", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return [];
  }
};

export const createPatient = async (param: PatientCreateParam): Promise<Patient> => {
  try {
    const response = await fetch("/api/practice/patients/create", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const getAppointmentTypes = async (param: UserParam): Promise<AppointmentType[]> => {
  try {
    const response = await fetch("/api/practice/appointment-type", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    const types = await response.json();
    return Promise.resolve(types);
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const getProviders = async (param: UserParam): Promise<Provider[]> => {
  try {
    const response = await fetch("/api/practice/providers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    const providers = await response.json();
    return Promise.resolve(providers);
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const getOperatories = async (param: UserParam): Promise<Operatory[]> => {
  try {
    const response = await fetch("/api/practice/operatories", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const getMonthAvailability = async (
  param: AvailabilityParam
): Promise<{ slots: TimeSlot[]; firstSlot: string }> => {
  try {
    const response = await fetch("/api/practice/appointments/monthslots", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    const slots = await response.json();
    return Promise.resolve(slots);
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const getDateAvailability = async (param: AvailabilityParam): Promise<TimeSlot[]> => {
  try {
    const response = await fetch("/api/practice/appointments/dateslots", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    const slots = await response.json();
    return Promise.resolve(slots);
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

// Availability
export const createAvailability = async (param: CreateAvailabilityParam): Promise<Availability> => {
  try {
    const response = await fetch("/api/practice/availabilities/create", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const updateAvailability = async (param: CreateAvailabilityParam): Promise<Availability> => {
  try {
    const response = await fetch("/api/practice/availabilities/update", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const deleteAvailability = async (param: DeleteAvailabilityParam): Promise<void> => {
  try {
    const response = await fetch("/api/practice/availabilities/delete", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const createPaymentIntent = async (param: UserParam): Promise<ClientSecret> => {
  try {
    const response = await fetch("/api/create-payment-intent", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};
